import type { WorkflowNodeEntity } from '@flowgram.ai/free-layout-editor';
import { useLanguageStore } from '$/store/languageStore';
import { TVFlowNodeNextTypeEnum } from '../config/node.config';

const sameTypeNodeNameCounters = new Map<string, number>();

function getStringName(name: unknown) {
  return typeof name === 'string' && name.trim() ? name.trim() : undefined;
}

function getBaseName(name: string) {
  return name.replace(/\s+\d+$/, '');
}

function getNameIndex(name: string) {
  const match = name.match(/\s+(\d+)$/);
  return match ? Number(match[1]) : 1;
}

function isSameNodeType(
  currentNode: WorkflowNodeEntity | undefined,
  nextNodeType: TVFlowNodeNextTypeEnum,
): currentNode is WorkflowNodeEntity {
  if (!currentNode) return false;
  return currentNode.flowNodeType === nextNodeType || currentNode.toJSON().type === nextNodeType;
}

export function getNextNodeNameByCurrentType({
  currentNode,
  nextNodeType,
  fallbackName,
  preferredBaseName,
}: {
  currentNode?: WorkflowNodeEntity;
  nextNodeType: TVFlowNodeNextTypeEnum;
  fallbackName: string;
  /** 显式指定新节点的业务名称前缀，同时继续沿用当前节点的序号。 */
  preferredBaseName?: string;
}) {
  const { languageStore } = useLanguageStore.getState();
  const currentName = isSameNodeType(currentNode, nextNodeType)
    ? getStringName(currentNode.toJSON().data?.name)
    : undefined;
  const baseName = getBaseName(
    getStringName(preferredBaseName)
      ?? currentName
      ?? languageStore.smartcatTranslationText((v) => v[fallbackName as keyof typeof v]),
  );
  const counterKey = `${nextNodeType}:${baseName}`;
  const currentCount = currentName ? getNameIndex(currentName) : 0;
  const nextCount = Math.max(sameTypeNodeNameCounters.get(counterKey) ?? 0, currentCount) + 1;

  sameTypeNodeNameCounters.set(counterKey, nextCount);
  return `${baseName} ${nextCount}`;
}
